<script>
import CardExerciseHorizontal from '@components/cards/card-exercise-horizontal.vue';
import ChatButton from '@components/chat/chat-button.vue';
import HeroWorkout from '@components/hero/hero-workout.vue';
import Layout from '@layouts/main.vue';
import CardCircuit from '@components/cards/card-circuit.vue';
import { breakpointsMixin } from '@mixins/breakpointsMixin';
import { programComputed, programMethods, clientComputed, workoutComputed, workoutMethods } from '@state/helpers';

export default {
  page: {
    title: 'Workout Detail',
    meta: [{ name: 'description', content: 'The Workout Detail page.' }],
  },
  mixins: [breakpointsMixin],
  components: {
    CardExerciseHorizontal,
    ChatButton,
    HeroWorkout,
    Layout,
    CardCircuit,
  },
  props: {
    workoutOption: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      scrollPosition: 5,
      heroScrollState: {
        minimize: false,
      },
      loading: false,
    };
  },
  async created() {
    this.loading = true;
    try {
      await this.getWorkoutDetails();
    } catch (err) {
      console.log(err);
    }
    this.loading = false;

    window.addEventListener('scroll', this.handleMobileScroll, false);
  },
  destroyed() {
    this.setWorkoutDetails(null);
    this.setProgramDay(null);
    window.removeEventListener('scroll', this.handleMobileScroll, false);
  },
  computed: {
    ...programComputed,
    ...clientComputed,
    ...workoutComputed,
    equipments() {
      if (this.workoutOption && this.workoutOption.equipmentList && this.workoutOption.equipmentList.length > 0) {
        return this.workoutOption.equipmentList.join(', ');
      } else {
        return '';
      }
    },
  },
  methods: {
    ...programMethods,
    ...workoutMethods,
    async getWorkoutDetails() {
      const payload = {
        programDay_id: this.workoutOption.programDay_id,
        org_id: this.orgId,
        trainer_id: this.trainerId,
        user_id: this.clientId,
      };
      try {
        await this.getProgramDay(payload);
      } catch (err) {
        console.log(err);
      }
    },
    handleMobileScroll() {
      if (!this.isDesktopView) {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.clientHeight;
        const scrollPercent = scrollTop / docHeight;
        const scrollPercentRounded = Math.round(scrollPercent * 100);
        const heroContainer = document.getElementById('hero-container');
        const heroContainerHeight = 68 - scrollPercentRounded;

        heroContainer.style.height = heroContainerHeight + '%';

        if (heroContainerHeight <= 26) {
          this.heroScrollState.minimize = true;
        }
        if (heroContainerHeight > 26) {
          this.heroScrollState.minimize = false;
        }
        if (heroContainerHeight <= 15) {
          heroContainer.style.backgroundColor = 'black';
          heroContainer.style.backgroundPosition = '-9999px -9999px';
        }
        if (heroContainerHeight > 15) {
          heroContainer.style.backgroundColor = 'black';
          heroContainer.style.backgroundPosition = 'center';
        }
      }
    },
    async onStartWorkout() {
      if (this.workoutOption && this.workoutOption.programDay_id) {
        this.setIsLoadingExercise(true);
        await this.getWorkoutDetails();
        await this.startWorkout({
          programDayId: this.workoutOption.programDay_id,
        });
        this.$router
          .push({
            name: 'exercise',
            params: {
              id: this.selectedWorkout.startingExercise._id,
              workout: { ...this.selectedWorkout, programDayId: this.workoutOption.programDay_id },
              prevRoute: this.$route.name,
            },
          })
          .then(() => this.setIsLoadingExercise(false));
      }
    },
  },
};
</script>

<template>
  <Layout>
    <div v-if="loading || isLoadingExercise" class="loading-wrapper">
      <BaseLoadingSpinner />
    </div>
    <div
      v-if="!loading && !isLoadingExercise && workoutDetails"
      :class="['workout-detail', { 'is-desktop': isDesktopView }]"
      ref="workoutDetail"
    >
      <ChatButton v-if="isDesktopView" />
      <HeroWorkout
        :scrollState="heroScrollState"
        :workout="workoutDetails"
        :equipments="equipments"
        @start-workout="onStartWorkout"
      />
      <div v-if="workoutDetails.circuits" :class="['content-wrapper', { minimize: heroScrollState.minimize }]">
        <template v-for="(circuit, i) in workoutDetails.circuits">
          <CardCircuit v-if="isDesktopView" :circuit="circuit" :key="circuit._id + i" />
          <div v-if="!isDesktopView" class="list-exercise-minimal" :key="circuit._id + i">
            <h5>{{ circuit.label }} • {{ circuit.circuitReps }} {{ circuit.circuitReps > 1 ? 'Laps' : 'Lap' }}</h5>
            <CardExerciseHorizontal
              v-for="(exercise, j) in circuit.exercises"
              :key="exercise._id + j"
              :exercise="exercise"
              :circuitIndex="i"
              :exerciseIndex="j"
            />
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.workout-detail {
  .content-wrapper {
    padding: 2.4rem 1.4rem;
    padding-top: 70vh;
    @include media('lg', null) {
      padding-left: 2.4rem;
      padding-right: 2.4rem;
    }

    .list-exercise-minimal h5 {
      color: #333;
      font-family: $secondary-font-family;
      font-size: 1.7rem;
      font-weight: 500;
      line-height: 1.7rem;
      margin-bottom: 1.5rem;
      margin-top: 3rem;
      text-transform: capitalize;
      transition: margin-top 0.5s ease-in-out;
    }
  }

  &.is-desktop {
    .content-wrapper {
      padding-top: 1.5vh;
    }
    .workout-hero {
      ::v-deep .hero-container {
        position: relative;
        .btn-start-workout {
          bottom: -2.5rem;
        }
      }
    }
  }
}

.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 30vh;
  width: 100%;

  ::v-deep .loading {
    height: 5rem;
    width: 5rem;
  }
}
</style>
